import React, { PropsWithChildren } from 'react';
import { useRequestAnimationFrame } from '../../../shared/hooks/useRequestAnimationFrame';
import { roundTime } from '../../utils/math';
import { useEventTimestamp } from '../hooks/useEventTimestamp';
import { useEventVoltage } from '../hooks/useEventVoltage';
import { useStrip } from '../hooks/useStrip';
import { Placeholder } from './Placeholder';
import { usePreventScroll } from '../hooks/usePreventScroll';

export type EventHandler = (
  key: keyof WindowEventMap,
  timestamp: number,
  voltage: number,
) => void;

type Props = PropsWithChildren<{
  disabled?: boolean;
  eventHandler: EventHandler;
}>;

export const StripInteractions: React.FC<Props> = ({
  disabled = false,
  eventHandler,
  children,
}) => {
  const ref = usePreventScroll<SVGGElement>();
  const { xScale } = useStrip();
  const getTimestamp = useEventTimestamp();
  const getVoltage = useEventVoltage();

  const debouncedEventHandler = useRequestAnimationFrame(eventHandler);

  const onMouseEvent = (
    key: keyof WindowEventMap,
    event: React.MouseEvent<SVGGElement>,
  ) => {
    if (event.defaultPrevented) {
      return;
    }
    debouncedEventHandler(key, getTimestamp(event), getVoltage(event));
  };

  const onWheelEvent = (
    key: keyof WindowEventMap,
    event: React.WheelEvent<SVGGElement>,
  ) => {
    debouncedEventHandler(
      key,
      roundTime(xScale.invert(event.deltaY) - xScale.invert(0)),
      0,
    );
  };

  return (
    <g
      className='strip-interaction'
      ref={ref}
      style={{ pointerEvents: disabled ? 'none' : undefined }}
      onClick={(event) => onMouseEvent('click', event)}
      onMouseDown={(event) => onMouseEvent('mousedown', event)}
      onMouseMove={(event) => onMouseEvent('mousemove', event)}
      onMouseUp={(event) => onMouseEvent('mouseup', event)}
      onMouseEnter={(event) => onMouseEvent('mouseenter', event)}
      onWheel={(event) => onWheelEvent('wheel', event)}
    >
      <Placeholder />
      {children}
    </g>
  );
};
